import React, { useState, useEffect } from 'react';
import { Users, DollarSign, Activity, TrendingUp, Search, ExternalLink, ChevronDown, ChevronUp, BarChart3, RefreshCw, Receipt, Briefcase, Gift, Download, Check, Undo2 } from 'lucide-react';
import { apiCall } from '../utils/api';

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'revenue', label: 'Revenue', icon: DollarSign },
  { id: 'pnl', label: 'P&L', icon: Receipt },
];

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [spendRefreshing, setSpendRefreshing] = useState(false);
  const [spendRefreshMsg, setSpendRefreshMsg] = useState(null);

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { if (activeTab === 'users') loadUsers(); }, [activeTab, sortField, sortOrder, search]);
  useEffect(() => { if (activeTab === 'revenue') loadRevenue(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'pnl') loadPnl(); }, [activeTab]);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/dashboard');
      const data = await res.json();
      if (data.success) setDashboard(data.dashboard);
    } catch (e) { console.error('Dashboard error:', e); }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const res = await apiCall(`/api/admin/users?sort=${sortField}&order=${sortOrder}&search=${encodeURIComponent(search)}`);
      const data = await res.json();
      if (data.success) setUsers(data.users);
    } catch (e) { console.error('Users error:', e); }
  };

  const loadRevenue = async () => {
    try {
      const res = await apiCall('/api/admin/revenue');
      const data = await res.json();
      if (data.success) setRevenue(data.revenue);
    } catch (e) { console.error('Revenue error:', e); }
  };

  const loadPnl = async () => {
    try {
      const res = await apiCall('/api/admin/pnl');
      const data = await res.json();
      if (data.success) setPnl(data.pnl);
    } catch (e) { console.error('P&L error:', e); }
  };

  const loadUserDetail = async (userId) => {
    try {
      const res = await apiCall(`/api/admin/users/${userId}`);
      const data = await res.json();
      if (data.success) {
        setUserDetail(data);
        setSelectedUser(userId);
      }
    } catch (e) { console.error('User detail error:', e); }
  };

  const refreshAdSpendCache = async () => {
    if (spendRefreshing) return;
    setSpendRefreshing(true);
    setSpendRefreshMsg(null);
    try {
      const res = await apiCall('/api/admin/refresh-ad-spend-cache', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setSpendRefreshMsg('Refresh started in background. Reload in ~1-2 minutes to see updated values.');
        setTimeout(() => setSpendRefreshMsg(null), 8000);
      } else {
        setSpendRefreshMsg('Refresh failed. Check server logs.');
      }
    } catch (e) {
      console.error('Refresh ad spend error:', e);
      setSpendRefreshMsg('Refresh failed. Check server logs.');
    }
    setSpendRefreshing(false);
  };

  const togglePartner = async (e, userId, currentStatus) => {
    e.stopPropagation();
    try {
      const res = await apiCall('/api/admin/set-partner', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, isPartner: !currentStatus })
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_partner: data.isPartner } : u));
      }
    } catch (e) { console.error('Toggle partner error:', e); }
  };

  // One-click upgrade: turns the row's user into an approved affiliate. The
  // backend is idempotent, so re-clicking on an already-affiliate user is a
  // no-op (just makes sure they're approved). We do not provide a "remove
  // affiliate" path here — for those rare cases use the Affiliates Admin page.
  const makeAffiliate = async (e, userId, currentStatus) => {
    e.stopPropagation();
    if (currentStatus === 'approved' || currentStatus === 'paused') {
      window.alert('This user is already an affiliate. Manage them from the Affiliates Admin page.');
      return;
    }
    if (!window.confirm('Turn this user into an approved affiliate? They will be able to earn commission immediately.')) return;
    try {
      const res = await apiCall(`/api/admin/users/${userId}/make-affiliate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (data.success) {
        setUsers(prev => prev.map(u => u.id === userId
          ? { ...u, affiliate_status: 'approved', affiliate_ref_code: data.affiliate?.refCode }
          : u
        ));
      } else {
        window.alert(data.error || 'Failed to make affiliate');
      }
    } catch (err) {
      console.error('Make affiliate error:', err);
      window.alert('Failed to make affiliate');
    }
  };

  // Toggle the partner organization's "last_invoiced_at" stamp. Used on the
  // Revenue tab while we still send partner invoices manually outside Stripe.
  const markPartnerInvoiced = async (orgId, currentlyInvoicedThisMonth) => {
    try {
      const res = await apiCall(`/api/admin/partners/${orgId}/mark-invoiced`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clear: !!currentlyInvoicedThisMonth })
      });
      const data = await res.json();
      if (!data.success) {
        window.alert(data.error || 'Failed to update invoice status');
        return;
      }
      // Mutate the local revenue partners array so the UI updates without a
      // full re-fetch (avoids the "loading… cards flash" feeling).
      setRevenue(prev => prev ? {
        ...prev,
        partners: (prev.partners || []).map(p =>
          p.orgId === orgId ? { ...p, lastInvoicedAt: data.lastInvoicedAt } : p
        )
      } : prev);
    } catch (err) {
      console.error('Mark invoiced error:', err);
      window.alert('Failed to update invoice status');
    }
  };

  // Returns true when `lastInvoicedAt` falls in the current calendar month —
  // used to flip the row UI between "Mark invoiced" and "Unmark / undo".
  const isInvoicedThisMonth = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };

  // Build a CSV download from the revenue.partners array. Client-side so we
  // don't need a separate backend endpoint — the data is already on screen.
  const exportPartnersCsv = () => {
    if (!revenue?.partners?.length) return;
    const rows = revenue.partners;
    const monthLabel = new Date().toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
    const header = [
      'Partner', 'Owner name', 'Owner email',
      'Active clients', 'Dormant clients', 'Total linked',
      'Starter', 'Growth', 'Scale', 'Enterprise',
      'Discount %', 'Monthly fee (EUR)',
      'Subscription status', 'Cancels at period end', 'Next invoice',
      'Last invoiced', 'Invoiced this month',
      'Stripe customer ID', 'Stripe subscription ID'
    ];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.map(escape).join(',')];
    for (const p of rows) {
      lines.push([
        p.name,
        p.ownerName || '',
        p.ownerEmail || '',
        p.activeBilled || 0,
        p.dormant || 0,
        p.totalLinked || 0,
        (p.tierCounts && p.tierCounts.starter) || 0,
        (p.tierCounts && p.tierCounts.growth) || 0,
        (p.tierCounts && p.tierCounts.scale) || 0,
        (p.tierCounts && p.tierCounts.enterprise) || 0,
        p.discountPercent || 0,
        (p.mrr || 0).toFixed(2),
        p.subscriptionStatus || 'inactive',
        p.cancelAtPeriodEnd ? 'yes' : 'no',
        p.currentPeriodEnd ? new Date(p.currentPeriodEnd).toISOString().slice(0, 10) : '',
        p.lastInvoicedAt ? new Date(p.lastInvoicedAt).toISOString().slice(0, 10) : '',
        isInvoicedThisMonth(p.lastInvoicedAt) ? 'yes' : 'no',
        p.stripeCustomerId || '',
        p.stripeSubscriptionId || ''
      ].map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const dateSlug = new Date().toISOString().slice(0, 10);
    a.download = `bidoryx-partners-${dateSlug}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    void monthLabel; // reserved for a future header line
  };

  const tierColor = (tier) => {
    const colors = { trial: '#f59e0b', starter: '#3b82f6', growth: '#10b981', scale: '#8b5cf6', enterprise: '#ec4899' };
    return colors[tier] || '#6b7280';
  };

  const statusColor = (status) => {
    const colors = { active: '#10b981', trialing: '#f59e0b', expired: '#ef4444', past_due: '#ef4444', canceled: '#6b7280' };
    return colors[status] || '#6b7280';
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
  const formatCurrency = (v) => new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(v || 0);

  const cardStyle = {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 12, padding: 20
  };

  if (loading && !dashboard) {
    return (
      <div style={{ padding: 30, textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
        <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 12px' }} />
        Loading admin dashboard...
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 30px', maxWidth: 1200 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ color: 'white', fontSize: 22, fontWeight: 700, margin: 0 }}>Admin Dashboard</h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, margin: '4px 0 0' }}>Manage users, subscriptions and revenue</p>
        </div>
        <button onClick={loadDashboard} style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)', color: 'white', padding: '8px 16px',
          borderRadius: 8, cursor: 'pointer', fontSize: 13
        }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 4 }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '10px 20px', borderRadius: 8,
            border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
            background: activeTab === tab.id ? 'rgba(99,102,241,0.3)' : 'transparent',
            color: activeTab === tab.id ? 'white' : 'rgba(255,255,255,0.5)'
          }}>
            <tab.icon size={15} /> {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && dashboard && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            {[
              { label: 'Total Users', value: dashboard.totalUsers, icon: Users, color: '#3b82f6' },
              { label: 'MRR', value: formatCurrency(dashboard.mrr), icon: DollarSign, color: '#10b981' },
              { label: 'New This Month', value: dashboard.newSignupsThisMonth, icon: TrendingUp, color: '#f59e0b' },
              { label: 'Active (7d)', value: dashboard.activeUsersLast7Days, icon: Activity, color: '#8b5cf6' },
            ].map((kpi, i) => (
              <div key={i} style={cardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>{kpi.label}</div>
                    <div style={{ color: 'white', fontSize: 26, fontWeight: 700 }}>{kpi.value}</div>
                  </div>
                  <div style={{ background: `${kpi.color}20`, padding: 8, borderRadius: 8 }}>
                    <kpi.icon size={18} style={{ color: kpi.color }} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div style={cardStyle}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Tier Breakdown</h3>
              {(dashboard.tierBreakdown || []).map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: tierColor(row.subscription_tier) }} />
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, textTransform: 'capitalize' }}>{row.subscription_tier || 'Unknown'}</span>
                  </div>
                  <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>{row.count}</span>
                </div>
              ))}
            </div>

            <div style={cardStyle}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Usage This Month</h3>
              {[
                { label: 'API Calls', value: (dashboard.usageThisMonth?.apiCalls || 0).toLocaleString() },
                { label: 'Optimizations', value: (dashboard.usageThisMonth?.optimizations || 0).toLocaleString() },
                { label: 'Campaigns Created', value: (dashboard.usageThisMonth?.campaigns || 0).toLocaleString() },
              ].map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>{item.label}</span>
                  <span style={{ color: 'white', fontWeight: 600, fontSize: 14 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: 'rgba(255,255,255,0.3)' }} />
              <input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by email or name..."
                style={{
                  width: '100%', padding: '10px 10px 10px 36px', borderRadius: 8,
                  border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.05)',
                  color: 'white', fontSize: 13, outline: 'none'
                }}
              />
            </div>
            <button
              onClick={refreshAdSpendCache}
              disabled={spendRefreshing}
              title="Refresh cached 30-day ad spend for every user (runs in background)"
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: spendRefreshing ? 'rgba(255,255,255,0.05)' : 'rgba(99,102,241,0.2)',
                border: '1px solid rgba(99,102,241,0.4)', color: 'white',
                padding: '10px 16px', borderRadius: 8, fontSize: 13,
                cursor: spendRefreshing ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                opacity: spendRefreshing ? 0.6 : 1
              }}
            >
              <RefreshCw size={14} className={spendRefreshing ? 'animate-spin' : ''} />
              {spendRefreshing ? 'Refreshing...' : 'Refresh Spend Cache'}
            </button>
          </div>
          {spendRefreshMsg && (
            <div style={{
              marginBottom: 12, padding: '10px 14px', borderRadius: 8,
              background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)',
              color: 'rgba(255,255,255,0.85)', fontSize: 13
            }}>
              {spendRefreshMsg}
            </div>
          )}

          {/* User detail modal */}
          {selectedUser && userDetail && (
            <div style={{ ...cardStyle, marginBottom: 16, borderColor: 'rgba(99,102,241,0.3)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, margin: 0 }}>
                  {userDetail.user.full_name || userDetail.user.email}
                </h3>
                <button onClick={() => { setSelectedUser(null); setUserDetail(null); }} style={{
                  background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 18
                }}>x</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Email</div><div style={{ color: 'white', fontSize: 13 }}>{userDetail.user.email}</div></div>
                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Tier</div><div style={{ color: tierColor(userDetail.user.subscription_tier), fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{userDetail.user.subscription_tier}</div></div>
                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Status</div><div style={{ color: statusColor(userDetail.user.subscription_status), fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>{userDetail.user.subscription_status}</div></div>
                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Signed up</div><div style={{ color: 'white', fontSize: 13 }}>{formatDate(userDetail.user.created_at)}</div></div>
                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>A/B Tests</div><div style={{ color: 'white', fontSize: 13 }}>{userDetail.stats.abTests}</div></div>
                <div><div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Recommendations Applied</div><div style={{ color: 'white', fontSize: 13 }}>{userDetail.stats.recommendationsApplied}</div></div>
              </div>
              {userDetail.connections.length > 0 && (
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>Connected Accounts</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {userDetail.connections.map((c, i) => (
                      <span key={i} style={{ background: 'rgba(255,255,255,0.08)', padding: '4px 10px', borderRadius: 6, fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                        {c.account_name || c.customer_id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {userDetail.usageHistory.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8, fontWeight: 600 }}>Usage History</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
                    {userDetail.usageHistory.slice(0, 6).map((u, i) => (
                      <div key={i} style={{ background: 'rgba(255,255,255,0.04)', padding: '8px 10px', borderRadius: 6, fontSize: 12 }}>
                        <div style={{ color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{u.month}</div>
                        <div style={{ color: 'white' }}>{u.api_calls} calls, {u.optimizations_generated} opts, {u.campaigns_created} campaigns</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Users table */}
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  {[
                    { key: 'email', label: 'User' },
                    { key: 'subscription_tier', label: 'Tier' },
                    { key: 'subscription_status', label: 'Status' },
                    { key: 'is_partner', label: 'Partner' },
                    { key: 'affiliate_status', label: 'Affiliate' },
                    { key: 'cached_ad_spend', label: 'Ad Spend (30d)' },
                    { key: 'accounts', label: 'Accounts' },
                    { key: 'usage', label: 'API Calls' },
                    { key: 'created_at', label: 'Signed Up' },
                  ].map(col => (
                    <th key={col.key} onClick={() => {
                      if (['email', 'subscription_tier', 'cached_ad_spend', 'created_at', 'updated_at'].includes(col.key)) {
                        if (sortField === col.key) setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
                        else { setSortField(col.key); setSortOrder('DESC'); }
                      }
                    }} style={{
                      padding: '12px 16px', textAlign: 'left', color: 'rgba(255,255,255,0.5)',
                      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
                      cursor: ['email', 'subscription_tier', 'cached_ad_spend', 'created_at'].includes(col.key) ? 'pointer' : 'default'
                    }}>
                      {col.label}
                      {sortField === col.key && (sortOrder === 'ASC' ? <ChevronUp size={12} style={{ marginLeft: 4 }} /> : <ChevronDown size={12} style={{ marginLeft: 4 }} />)}
                    </th>
                  ))}
                  <th style={{ padding: '12px 16px', width: 50 }} />
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} onClick={() => loadUserDetail(user.id)}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', cursor: 'pointer', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ color: 'white', fontSize: 13, fontWeight: 500 }}>{user.full_name || '-'}</div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{user.email}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        background: `${tierColor(user.subscription_tier)}20`, color: tierColor(user.subscription_tier),
                        padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, textTransform: 'capitalize'
                      }}>{user.subscription_tier || '-'}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ color: statusColor(user.subscription_status), fontSize: 12, fontWeight: 500, textTransform: 'capitalize' }}>
                        {user.subscription_status || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={(e) => togglePartner(e, user.id, user.is_partner)}
                        style={{
                          background: user.is_partner ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255,255,255,0.05)',
                          color: user.is_partner ? '#10b981' : 'rgba(255,255,255,0.4)',
                          border: `1px solid ${user.is_partner ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255,255,255,0.1)'}`,
                          borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                          cursor: 'pointer', transition: 'all 0.15s'
                        }}
                      >
                        {user.is_partner ? 'Partner' : '—'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      {(() => {
                        const status = user.affiliate_status;
                        const colorMap = {
                          approved: { bg: 'rgba(139,92,246,0.15)', fg: '#8b5cf6', border: 'rgba(139,92,246,0.3)' },
                          pending:  { bg: 'rgba(245,158,11,0.15)', fg: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
                          paused:   { bg: 'rgba(107,114,128,0.15)', fg: '#9ca3af', border: 'rgba(107,114,128,0.3)' },
                          rejected: { bg: 'rgba(239,68,68,0.15)', fg: '#ef4444', border: 'rgba(239,68,68,0.3)' }
                        };
                        const c = colorMap[status] || { bg: 'rgba(255,255,255,0.05)', fg: 'rgba(255,255,255,0.4)', border: 'rgba(255,255,255,0.1)' };
                        const label = status
                          ? status.charAt(0).toUpperCase() + status.slice(1)
                          : '+ Make';
                        return (
                          <button
                            onClick={(e) => makeAffiliate(e, user.id, status)}
                            title={status ? `Affiliate ref code: ${user.affiliate_ref_code || '—'}` : 'Click to make this user an affiliate'}
                            style={{
                              background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
                              borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600,
                              cursor: 'pointer', transition: 'all 0.15s'
                            }}
                          >
                            {label}
                          </button>
                        );
                      })()}
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13 }}>
                      {user.cached_ad_spend ? (
                        <span style={{ color: (() => {
                          const spend = parseFloat(user.cached_ad_spend);
                          const limits = { starter: 25000, growth: 100000, scale: 250000 };
                          const limit = limits[user.subscription_tier];
                          if (!limit) return 'rgba(255,255,255,0.7)';
                          if (spend > limit) return '#ef4444';
                          if (spend > limit * 0.8) return '#f59e0b';
                          return 'rgba(255,255,255,0.7)';
                        })(), fontWeight: 500 }}>
                          €{parseFloat(user.cached_ad_spend).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </span>
                      ) : (
                        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{user.accounts_connected}</td>
                    <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{(user.api_calls || 0).toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{formatDate(user.created_at)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {user.stripe_customer_id && (
                        <a href={`https://dashboard.stripe.com/customers/${user.stripe_customer_id}`}
                          target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          style={{ color: 'rgba(255,255,255,0.3)' }}
                          title="View in Stripe"
                        >
                          <ExternalLink size={14} />
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>No users found</div>
            )}
          </div>
        </div>
      )}

      {/* Revenue Tab */}
      {activeTab === 'revenue' && revenue && (
        <div>
          {/* Top KPI cards: Total MRR (with direct/partner split), ARR, Paying Customers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Monthly Recurring Revenue</div>
              <div style={{ color: '#10b981', fontSize: 28, fontWeight: 700 }}>{formatCurrency(revenue.totalMrr)}</div>
              <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                <span>Direct: <span style={{ color: 'rgba(255,255,255,0.85)' }}>{formatCurrency(revenue.directMrr || 0)}</span></span>
                <span style={{ color: 'rgba(255,255,255,0.2)' }}>·</span>
                <span>Partners: <span style={{ color: 'rgba(255,255,255,0.85)' }}>{formatCurrency(revenue.partnerMrr || 0)}</span></span>
              </div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Annual Run Rate</div>
              <div style={{ color: 'white', fontSize: 28, fontWeight: 700 }}>{formatCurrency(revenue.arr)}</div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>Based on combined MRR</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Customers</div>
              <div style={{ color: 'white', fontSize: 28, fontWeight: 700 }}>{revenue.totalPayingUsers}<span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 16, fontWeight: 500 }}> + {revenue.activePartnersCount || 0} partners</span></div>
              <div style={{ marginTop: 10, fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                {revenue.partnersCount || 0} partner organizations linked
              </div>
            </div>
          </div>

          {/* Direct customers: MRR by tier + paying users list */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={cardStyle}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Direct MRR by Tier</h3>
              {(revenue.byTier || []).map((tier, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: tierColor(tier.tier) }} />
                    <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>{tier.tierName}</span>
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>({tier.users} users)</span>
                  </div>
                  <span style={{ color: '#10b981', fontWeight: 600, fontSize: 14 }}>{formatCurrency(tier.mrr)}</span>
                </div>
              ))}
              {(revenue.byTier || []).length === 0 && (
                <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, padding: 8 }}>No direct subscriptions yet</div>
              )}
            </div>

            <div style={cardStyle}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Paying Users (direct)</h3>
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {(revenue.payingUsers || []).map((user, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                    <div>
                      <div style={{ color: 'white', fontSize: 13 }}>{user.name || user.email}</div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                        <span style={{ color: tierColor(user.tier), textTransform: 'capitalize' }}>{user.tier}</span> since {formatDate(user.since)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ color: '#10b981', fontWeight: 600, fontSize: 13 }}>{formatCurrency(user.monthlyRevenue)}/mo</span>
                      {user.stripeCustomerId && (
                        <a href={`https://dashboard.stripe.com/customers/${user.stripeCustomerId}`}
                          target="_blank" rel="noopener noreferrer"
                          style={{ color: 'rgba(255,255,255,0.3)' }}>
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {(revenue.payingUsers || []).length === 0 && (
                  <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textAlign: 'center', padding: 20 }}>No paying users yet</div>
                )}
              </div>
            </div>
          </div>

          {/* Partner billing breakdown */}
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Briefcase size={16} style={{ color: '#8b5cf6' }} />
                Partners — what to invoice this month
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                  Total: <span style={{ color: '#10b981', fontWeight: 600 }}>{formatCurrency(revenue.partnerMrr || 0)}</span> / month
                </span>
                <button
                  onClick={exportPartnersCsv}
                  disabled={!revenue.partners || revenue.partners.length === 0}
                  title="Download partner billing as CSV"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    background: 'rgba(139,92,246,0.15)', color: '#8b5cf6',
                    border: '1px solid rgba(139,92,246,0.3)',
                    borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    cursor: (!revenue.partners || revenue.partners.length === 0) ? 'not-allowed' : 'pointer',
                    opacity: (!revenue.partners || revenue.partners.length === 0) ? 0.4 : 1
                  }}
                >
                  <Download size={12} /> Export CSV
                </button>
              </div>
            </div>

            {(!revenue.partners || revenue.partners.length === 0) ? (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, textAlign: 'center', padding: 20 }}>
                No partner organizations yet
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                      {['Partner', 'Owner', 'Active clients', 'Tier mix', 'Discount', 'MRR', 'Sub status', 'Next invoice', 'Last invoiced', ''].map(h => (
                        <th key={h} style={{ textAlign: h === 'MRR' || h === 'Active clients' ? 'right' : 'left', padding: '10px 12px', color: 'rgba(255,255,255,0.5)', fontSize: 11, textTransform: 'uppercase', fontWeight: 500 }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {revenue.partners.map(p => {
                      const tierMix = ['starter', 'growth', 'scale', 'enterprise']
                        .map(k => ({ k, n: (p.tierCounts && p.tierCounts[k]) || 0 }))
                        .filter(t => t.n > 0);
                      const subColor = statusColor(p.subscriptionStatus);
                      const billable = (p.mrr || 0) > 0;
                      const invoicedThisMonth = isInvoicedThisMonth(p.lastInvoicedAt);
                      return (
                        <tr key={p.orgId} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '12px', color: 'white', fontWeight: 500 }}>{p.name}</td>
                          <td style={{ padding: '12px', color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>
                            {p.ownerName || p.ownerEmail || '—'}
                            {p.ownerName && p.ownerEmail && (
                              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{p.ownerEmail}</div>
                            )}
                          </td>
                          <td style={{ padding: '12px', color: 'white', textAlign: 'right', fontWeight: 600 }}>
                            {p.activeBilled}
                            {p.dormant > 0 && (
                              <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 400 }}> +{p.dormant} dormant</span>
                            )}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {tierMix.length === 0 ? (
                              <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>—</span>
                            ) : (
                              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                {tierMix.map(t => (
                                  <span key={t.k} style={{
                                    background: `${tierColor(t.k)}20`,
                                    color: tierColor(t.k),
                                    padding: '2px 8px',
                                    borderRadius: 4,
                                    fontSize: 11,
                                    textTransform: 'capitalize',
                                    fontWeight: 500
                                  }}>{t.k} ×{t.n}</span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '12px', color: p.discountPercent > 0 ? '#8b5cf6' : 'rgba(255,255,255,0.4)', fontSize: 12, fontWeight: p.discountPercent > 0 ? 600 : 400 }}>
                            {p.discountPercent > 0 ? `−${p.discountPercent}%` : '—'}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right', color: p.mrr > 0 ? '#10b981' : 'rgba(255,255,255,0.3)', fontWeight: 600 }}>
                            {p.mrr > 0 ? formatCurrency(p.mrr) : '—'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <span style={{ color: subColor, fontSize: 12, fontWeight: 500, textTransform: 'capitalize' }}>
                              {p.subscriptionStatus || 'inactive'}
                            </span>
                            {p.cancelAtPeriodEnd && (
                              <div style={{ color: '#ef4444', fontSize: 10, fontWeight: 500 }}>cancels at period end</div>
                            )}
                          </td>
                          <td style={{ padding: '12px', color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                            {formatDate(p.currentPeriodEnd)}
                          </td>
                          <td style={{ padding: '12px' }}>
                            {invoicedThisMonth ? (
                              <button
                                onClick={() => markPartnerInvoiced(p.orgId, true)}
                                title="Click to undo (mark as not invoiced)"
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  background: 'rgba(16,185,129,0.15)', color: '#10b981',
                                  border: '1px solid rgba(16,185,129,0.3)',
                                  borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 600,
                                  cursor: 'pointer'
                                }}
                              >
                                <Check size={11} /> {formatDate(p.lastInvoicedAt)}
                              </button>
                            ) : (
                              <button
                                onClick={() => markPartnerInvoiced(p.orgId, false)}
                                disabled={!billable}
                                title={billable
                                  ? `Mark as invoiced for ${formatCurrency(p.mrr)}/mo`
                                  : 'No MRR to invoice yet'}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 5,
                                  background: 'rgba(255,255,255,0.04)',
                                  color: billable ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.25)',
                                  border: `1px solid ${billable ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.05)'}`,
                                  borderRadius: 4, padding: '3px 8px', fontSize: 11, fontWeight: 500,
                                  cursor: billable ? 'pointer' : 'not-allowed'
                                }}
                              >
                                {p.lastInvoicedAt ? (
                                  <><Undo2 size={11} /> {formatDate(p.lastInvoicedAt)}</>
                                ) : (
                                  'Mark invoiced'
                                )}
                              </button>
                            )}
                          </td>
                          <td style={{ padding: '12px', textAlign: 'right' }}>
                            {p.stripeCustomerId && (
                              <a href={`https://dashboard.stripe.com/customers/${p.stripeCustomerId}`}
                                target="_blank" rel="noopener noreferrer"
                                title="Open in Stripe"
                                style={{ color: 'rgba(255,255,255,0.4)' }}>
                                <ExternalLink size={14} />
                              </a>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Affiliate commission summary */}
          {revenue.affiliateSummary && (
            <div style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Gift size={16} style={{ color: '#ec4899' }} />
                  Affiliate program
                </h3>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                  {revenue.affiliateSummary.affiliatesCount} active affiliate{revenue.affiliateSummary.affiliatesCount === 1 ? '' : 's'}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6 }}>Pending payout</div>
                  <div style={{ color: '#f59e0b', fontSize: 22, fontWeight: 700 }}>{formatCurrency((revenue.affiliateSummary.pendingPayoutCents || 0) / 100)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>
                    {revenue.affiliateSummary.eligibleForPayoutCount} ready (≥ {formatCurrency((revenue.affiliateSummary.minPayoutCents || 10000) / 100)})
                  </div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6 }}>This month earned</div>
                  <div style={{ color: '#ec4899', fontSize: 22, fontWeight: 700 }}>{formatCurrency((revenue.affiliateSummary.currentMonthCommissionCents || 0) / 100)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>commission earned in {new Date().toLocaleDateString('nl-NL', { month: 'long' })}</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6 }}>Lifetime commission</div>
                  <div style={{ color: 'white', fontSize: 22, fontWeight: 700 }}>{formatCurrency((revenue.affiliateSummary.lifetimeCommissionCents || 0) / 100)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>net of reversals</div>
                </div>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 6 }}>Already paid out</div>
                  <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 22, fontWeight: 700 }}>{formatCurrency((revenue.affiliateSummary.paidOutCents || 0) / 100)}</div>
                  <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, marginTop: 4 }}>SEPA transfers settled</div>
                </div>
              </div>
              <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                Manage approvals, per-affiliate commissions and payout batches in the <span style={{ color: '#ec4899', fontWeight: 500 }}>Affiliates Admin</span> tab in the sidebar.
              </div>
            </div>
          )}
        </div>
      )}

      {/* P&L Tab */}
      {activeTab === 'pnl' && pnl && (
        <div>
          {/* Summary Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Revenue (MRR)</div>
              <div style={{ color: '#10b981', fontSize: 26, fontWeight: 700 }}>{formatCurrency(pnl.revenue.mrr)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Total Costs</div>
              <div style={{ color: '#ef4444', fontSize: 26, fontWeight: 700 }}>{formatCurrency(pnl.costs.total)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Net Profit</div>
              <div style={{ color: pnl.profit.gross >= 0 ? '#10b981' : '#ef4444', fontSize: 26, fontWeight: 700 }}>{formatCurrency(pnl.profit.gross)}</div>
            </div>
            <div style={cardStyle}>
              <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 8 }}>Profit Margin</div>
              <div style={{ color: pnl.profit.margin >= 0 ? '#10b981' : '#ef4444', fontSize: 26, fontWeight: 700 }}>{pnl.profit.margin}%</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {/* Revenue Breakdown */}
            <div style={cardStyle}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Revenue</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>Monthly Recurring Revenue</span>
                <span style={{ color: '#10b981', fontWeight: 600, fontSize: 14 }}>{formatCurrency(pnl.revenue.mrr)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Annualized (ARR)</span>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>{formatCurrency(pnl.revenue.arr)}</span>
              </div>
            </div>

            {/* Costs Breakdown */}
            <div style={cardStyle}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Costs</h3>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Fixed Costs</div>
              {(pnl.costs.fixed || []).map((cost, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>{cost.name}</span>
                  <span style={{ color: '#ef4444', fontWeight: 500, fontSize: 13 }}>
                    {cost.currency === 'USD' ? '$' : '€'}{cost.amount.toFixed(2)}
                  </span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600 }}>Subtotal Fixed</span>
                <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>{formatCurrency(pnl.costs.totalFixed)}</span>
              </div>

              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 12, marginBottom: 8 }}>Variable Costs</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>OpenAI API (estimated)</span>
                <span style={{ color: '#ef4444', fontWeight: 500, fontSize: 13 }}>{formatCurrency(pnl.costs.totalVariable)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', marginTop: 8, borderTop: '2px solid rgba(255,255,255,0.15)' }}>
                <span style={{ color: 'white', fontSize: 14, fontWeight: 700 }}>Total Costs</span>
                <span style={{ color: '#ef4444', fontWeight: 700, fontSize: 14 }}>{formatCurrency(pnl.costs.total)}</span>
              </div>
            </div>
          </div>

          {/* Per-User Breakdown */}
          <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <h3 style={{ color: 'white', fontSize: 15, fontWeight: 600, margin: 0 }}>Per-User Breakdown ({pnl.month})</h3>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  {['User', 'Tier', 'Revenue', 'API Calls', 'Optimizations', 'Est. Cost', 'Profit'].map(h => (
                    <th key={h} style={{ padding: '10px 16px', textAlign: h === 'User' ? 'left' : 'right', color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(pnl.userBreakdown || []).map((user, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ color: 'white', fontSize: 13, fontWeight: 500 }}>{user.name || user.email}</div>
                      <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{user.email}</div>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <span style={{
                        background: `${tierColor(user.tier)}20`, color: tierColor(user.tier),
                        padding: '2px 8px', borderRadius: 5, fontSize: 11, fontWeight: 600, textTransform: 'capitalize'
                      }}>{user.tier}</span>
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: user.revenue > 0 ? '#10b981' : 'rgba(255,255,255,0.3)', fontSize: 13, fontWeight: user.revenue > 0 ? 600 : 400 }}>
                      {user.revenue > 0 ? formatCurrency(user.revenue) : '-'}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                      {user.apiCalls.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>
                      {user.optimizations.toLocaleString()}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: user.estimatedCost > 0 ? '#ef4444' : 'rgba(255,255,255,0.3)', fontSize: 13 }}>
                      {user.estimatedCost > 0 ? formatCurrency(user.estimatedCost) : '-'}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right', color: user.profit >= 0 ? '#10b981' : '#ef4444', fontSize: 13, fontWeight: 600 }}>
                      {user.revenue > 0 || user.estimatedCost > 0 ? formatCurrency(user.profit) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.03)' }}>
                  <td style={{ padding: '12px 16px', color: 'white', fontWeight: 700, fontSize: 13 }}>Total</td>
                  <td />
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: '#10b981', fontWeight: 700, fontSize: 13 }}>{formatCurrency(pnl.revenue.mrr)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: 13 }}>{pnl.usage.totalApiCalls.toLocaleString()}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: 'white', fontWeight: 600, fontSize: 13 }}>{pnl.usage.totalOptimizations.toLocaleString()}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: '#ef4444', fontWeight: 700, fontSize: 13 }}>{formatCurrency(pnl.costs.totalVariable)}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right', color: pnl.profit.gross >= 0 ? '#10b981' : '#ef4444', fontWeight: 700, fontSize: 13 }}>{formatCurrency(pnl.profit.gross)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
